import Handlebars from 'handlebars';
import { customHandlebarsBlocks, initialCustomData } from './helpers';

export function addHandlebarsComponentType(editor: any) {
  editor.Components.addType('handlebars', {
    model: {
      defaults: {
        ...editor.Components.getType('default').model.prototype.defaults,
        droppable: true,
        editable: true,
        templateData: {},
      },
      init() {
        const ctx: any = this;
        if (!ctx.components().length) {
          const template = ctx.get('template');
          const data = ctx.get('templateData');
          if (Object.keys(data).length) {
            ctx.components(Handlebars.compile(template)(data));
          } else {
            ctx.components(template);
          }
        }
      },
      toHTML() {
        const ctx: any = this;
        if (ctx.components().length) {
          return editor.Components.getType('default').model.prototype.toHTML.apply(this, arguments);
        } else {
          return ctx.get('template');
        }
      },
    },
    view: editor.Components.getType('default').view,
  });
}

export function registerHandlebarsBlocks(editor: any) {
  addHandlebarsComponentType(editor);
  customHandlebarsBlocks.forEach((block) => {
    if (!editor.BlockManager) {
      console.error('BlockManager is not available in the editor instance');
      return;
    }
    const blockType = block.content?.type || 'handlebars';
    editor.BlockManager.add(block.id, {
      label: block.label,
      content: {
        type: blockType,
        template: block.template,
        templateData: block.defaultData || {},
        ...block.content,
      },
      category: block.category,
      attributes: { class: '' },
    });
  });
  editor.Panels.addPanel({
    id: 'template-variables',
    visible: true,
    buttons: [
      {
        id: 'open-variables',
        label: 'Variables',
        command: 'open-variables-manager',
        togglable: false,
        attributes: { title: 'Manage Template Variables' },
      },
    ],
  });
  if (!editor.customData) {
    editor.customData = editor.getConfig().customData || {};
  }
  // Add command to open variables manager with JSON input
  editor.Commands.add('open-variables-manager', {
    run: function (editor) {
      const currentVariables = Object.keys(editor.customData)?.length
        ? editor.customData
        : initialCustomData;

      const initialJson = JSON.stringify(currentVariables, null, 2);

      const variablesModal = document.createElement('div');
      variablesModal.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 9999;">
          <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 20px; border-radius: 5px; min-width: 500px;">
            <h3>Manage Template Variables (JSON)</h3>
            <div style="margin-bottom: 10px;">
              <textarea id="json-variables" style="width: 100%; height: 300px; font-family: monospace; padding: 10px;">${initialJson}</textarea>
              <div id="json-error" style="color: red; margin-top: 5px;"></div>
            </div>
            <button id="save-variables" style="margin-top: 10px; padding: 5px 10px;">Save</button>
            <button id="close-variables" style="margin-top: 10px; margin-left: 5px; padding: 5px 10px;">Close</button>
          </div>
        </div>
      `;

      document.body.appendChild(variablesModal);

      // Function to validate JSON
      const validateJson = (jsonString) => {
        try {
          JSON.parse(jsonString);
          return true;
        } catch (error) {
          return false;
        }
      };

      // Add live validation
      const jsonTextarea = document.getElementById('json-variables') as HTMLTextAreaElement;
      const errorDiv = document.getElementById('json-error');

      jsonTextarea.addEventListener('input', () => {
        if (validateJson(jsonTextarea.value)) {
          errorDiv!.textContent = '';
          jsonTextarea.style.border = '1px solid #ccc';
        } else {
          errorDiv!.textContent = 'Invalid JSON format';
          jsonTextarea.style.border = '1px solid red';
        }
      });

      // Handle save button
      document.getElementById('save-variables')?.addEventListener('click', () => {
        const jsonString = jsonTextarea.value;

        if (!validateJson(jsonString)) {
          errorDiv!.textContent = 'Cannot save: Invalid JSON format';
          return;
        }

        try {
          const newVariables = JSON.parse(jsonString);

          editor.customData = newVariables;

          editor.getComponents().forEach((component) => {
            const mergedData = {
              ...component.get('templateData'),
              ...newVariables,
            };

            // Update the component's template data
            component.set('templateData', mergedData);

            // Trigger re-rendering
            const template = component.get('template') || component.toHTML();
            if (template) {
              try {
                const html = Handlebars.compile(template)(mergedData);
                // Escape HTML entities
                const safeHtml = html.replace(/&(?!(amp|lt|gt|quot|apos|#\d+);)/g, '&amp;');
                component.components(safeHtml);
              } catch (e) {
                console.error('Error compiling template:', e);
              }
            }
          });

          document.body.removeChild(variablesModal);
        } catch (error: any) {
          errorDiv!.textContent = `Error: ${error?.message || 'Unknown error'}`;
        }
      });

      document.getElementById('close-variables')?.addEventListener('click', () => {
        document.body.removeChild(variablesModal);
      });
    },
  });
}
